// The client-facing "Questions" view. The agent attaches single/multi/text
// questions; the client answers here — tap choices, type, an optional "Other",
// and an optional/required image upload. Answers AUTOSAVE (debounced). Reuses
// FileRow for answer images. Built to read like a caring studio, not an AI form.
import { useEffect, useRef, useState } from 'react'
import { Check, Upload } from 'react-feather'
import type { Question, QuestionAnswer, UploadedFile } from '../types'
import { useFileUrl } from './FileUrlContext'
import { FileRow } from './Portal'

export const OTHER = '__other__'
const emptyAnswer = (): QuestionAnswer => ({ selected: [], other_text: '', text: '', files: [] })

// A question is "answered" only when ALL its obligations are met: a required
// choice/text must have content, AND a required image must have a file. So a
// "required image" question can't read as done with no image (the asset would be
// silently lost). Matches the server `answered` flag in drive-service.js.
export function isAnswered(q: Question): boolean {
  const a = q.answer
  if (!a) return false
  const hasImg = a.files.length > 0
  const hasContent = q.type === 'text' ? !!a.text.trim() : a.selected.length > 0
  const imageOk = q.allow_image !== 'required' || hasImg
  const contentOk = !q.required || hasContent
  return (hasContent || hasImg) && imageOk && contentOk
}

type SaveFn = (questionId: string, value: { selected?: string[]; other_text?: string; text?: string }) => void
type PickFn = (questionId: string, files: FileList | File[]) => void

export function QuestionsView({ questions, onSave, onPickImages, onOpenFile, onRemoveFile, readOnly = false, intro }: {
  questions: Question[]
  onSave: SaveFn
  onPickImages: PickFn
  onOpenFile: (f: UploadedFile) => void
  onRemoveFile: (f: UploadedFile) => void
  readOnly?: boolean
  intro?: string
}) {
  const total = questions.length
  const done = questions.filter(isAnswered).length
  const allDone = total > 0 && done === total
  return (
    <div className="rf-fade-in">
      <p className="text-sm" style={{ color: 'var(--color-ink-2)' }}>{intro ?? 'A few quick questions from your Reputifly team — your answers help us get it just right.'}</p>
      <div className="flex items-center gap-2 mt-2 mb-1">
        <div style={{ flex: 1, height: 4, borderRadius: 9999, background: 'var(--color-surface-2)' }}>
          <div style={{ width: `${total ? Math.round((done / total) * 100) : 0}%`, height: '100%', borderRadius: 9999, background: allDone ? 'var(--color-success)' : 'var(--color-primary)', transition: 'width .25s var(--ease)' }} />
        </div>
        <span className="text-xs" style={{ color: 'var(--color-faint)', whiteSpace: 'nowrap' }}>answered {done} of {total}</span>
      </div>

      <div className="grid gap-4 mt-3">
        {questions.map((q) => <QuestionCard key={q.id} q={q} onSave={onSave} onPickImages={onPickImages} onOpenFile={onOpenFile} onRemoveFile={onRemoveFile} readOnly={readOnly} />)}
      </div>

      {allDone && !readOnly && (
        <div className="card flex items-center gap-2 mt-4" style={{ padding: '12px 16px' }}>
          <Check size={18} color="var(--color-success)" style={{ flexShrink: 0 }} />
          <span className="text-sm" style={{ color: 'var(--color-ink-2)' }}>All set — thank you! You can close this, or tweak any answer above any time.</span>
        </div>
      )}
    </div>
  )
}

function QuestionCard({ q, onSave, onPickImages, onOpenFile, onRemoveFile, readOnly }: {
  q: Question; onSave: SaveFn; onPickImages: PickFn; onOpenFile: (f: UploadedFile) => void; onRemoveFile: (f: UploadedFile) => void; readOnly?: boolean
}) {
  const a = q.answer || emptyAnswer()
  const [selected, setSelected] = useState<string[]>(a.selected)
  const selectedRef = useRef<string[]>(a.selected)   // mirrors `selected` so rapid taps (same render frame) can't lose a toggle
  useEffect(() => { selectedRef.current = selected }, [selected])
  const [otherText, setOtherText] = useState(a.other_text)
  const [text, setText] = useState(a.text)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // debounced autosave — local state is the editing source so a background poll
  // never clobbers what the client is typing; the server reconciles progress.
  const commit = (patch: Partial<{ selected: string[]; other_text: string; text: string }>) => {
    const next = { selected: patch.selected ?? selected, other_text: patch.other_text ?? otherText, text: patch.text ?? text }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => onSave(q.id, next), 450)
  }
  const toggle = (id: string) => {
    if (readOnly) return
    const cur = selectedRef.current   // read the latest, not the render closure → rapid taps never drop one
    const next = q.type === 'single' ? [id] : (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])
    selectedRef.current = next        // update synchronously so a same-frame second tap sees it
    setSelected(next); commit({ selected: next })
  }
  const otherOn = selected.includes(OTHER)
  const hasImages = q.options.some((o) => o.image)

  return (
    <section className="card" style={{ padding: 18 }}>
      <div className="flex items-start gap-2">
        <h2 className="font-semibold flex-1" style={{ color: 'var(--color-ink)', fontSize: 15, lineHeight: 1.35 }}>{q.prompt}</h2>
        {(q.required || q.allow_image === 'required') && <span className="text-xs" style={{ color: 'var(--color-primary)', whiteSpace: 'nowrap', paddingTop: 3 }}>Required</span>}
      </div>

      {q.type !== 'text' && (
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: hasImages ? 'repeat(auto-fill, minmax(150px, 1fr))' : '1fr' }}>
          {q.options.map((o) => <OptionTile key={o.id} label={o.label} image={o.image} on={selected.includes(o.id)} multi={q.type === 'multi'} disabled={readOnly} onClick={() => toggle(o.id)} />)}
          {q.allow_other && <OptionTile label="Other…" on={otherOn} multi={q.type === 'multi'} disabled={readOnly} onClick={() => toggle(OTHER)} />}
        </div>
      )}
      {q.type !== 'text' && q.allow_other && otherOn && (
        <input className="input mt-2" placeholder="Type your answer" value={otherText} readOnly={readOnly}
          onChange={(e) => { setOtherText(e.target.value); commit({ other_text: e.target.value }) }} />
      )}

      {q.type === 'text' && (
        <textarea className="input mt-3" rows={3} placeholder={readOnly ? '(no answer)' : 'Type your answer'} value={text} style={{ resize: 'vertical' }} readOnly={readOnly}
          onChange={(e) => { setText(e.target.value); commit({ text: e.target.value }) }} />
      )}

      {q.allow_image !== 'no' && <ImageAnswer q={q} onPickImages={onPickImages} onOpenFile={onOpenFile} onRemoveFile={onRemoveFile} readOnly={readOnly} />}
    </section>
  )
}

function OptionTile({ label, image, on, multi, disabled, onClick }: { label: string; image?: string | null; on: boolean; multi: boolean; disabled?: boolean; onClick: () => void }) {
  const fileUrl = useFileUrl()
  return (
    <button onClick={onClick} disabled={disabled} className="text-left rf-pop-in" style={{
      border: `1.5px solid ${on ? 'var(--color-primary)' : 'var(--color-line)'}`,
      background: on ? 'var(--color-primary-soft)' : 'var(--color-surface)',
      borderRadius: 12, overflow: 'hidden', cursor: disabled ? 'default' : 'pointer', padding: 0,
    }}>
      {image && <div style={{ height: 84, background: 'var(--color-surface-2)' }}><img src={fileUrl(image)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>}
      <div className="flex items-center gap-2" style={{ padding: '10px 12px' }}>
        <span style={{ width: 18, height: 18, flexShrink: 0, borderRadius: multi ? 5 : 9999, border: `1.5px solid ${on ? 'var(--color-primary)' : 'var(--color-line-2)'}`, background: on ? 'var(--color-primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {on && <Check size={12} color="#fff" />}
        </span>
        <span style={{ fontSize: 14, color: on ? 'var(--color-primary)' : 'var(--color-ink-2)', fontWeight: on ? 600 : 500, overflowWrap: 'anywhere' }}>{label}</span>
      </div>
    </button>
  )
}

function ImageAnswer({ q, onPickImages, onOpenFile, onRemoveFile, readOnly }: {
  q: Question; onPickImages: PickFn; onOpenFile: (f: UploadedFile) => void; onRemoveFile: (f: UploadedFile) => void; readOnly?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [drag, setDrag] = useState(false)
  const files = q.answer?.files || []
  const needsImage = q.allow_image === 'required' && files.length === 0   // unfulfilled required image → accent it
  return (
    <div className="mt-3">
      {!readOnly && (
        <div onClick={() => inputRef.current?.click()} role="button" tabIndex={0}
          onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const imgs = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/')); if (imgs.length) onPickImages(q.id, imgs) }}
          className="flex items-center justify-center gap-2 cursor-pointer"
          style={{ border: `1.5px dashed ${drag || needsImage ? 'var(--color-primary)' : 'var(--color-line-2)'}`, background: drag ? 'var(--color-primary-soft)' : needsImage ? 'var(--color-primary-soft)' : 'var(--color-surface-2)', borderRadius: 12, padding: 12, transition: 'all .15s var(--ease)' }}>
          <Upload size={16} color="var(--color-primary)" />
          <span style={{ fontSize: 13, color: 'var(--color-primary)', fontWeight: 600 }}>Add image{q.allow_image === 'required' ? ' (required)' : q.allow_image === 'optional' ? ' (optional)' : ''}</span>
          <span className="text-xs" style={{ color: 'var(--color-faint)', fontWeight: 500 }}>or drag here</span>
          <input ref={inputRef} type="file" multiple hidden accept="image/*"
            onChange={(e) => { if (e.target.files?.length) onPickImages(q.id, e.target.files); e.currentTarget.value = '' }} />
        </div>
      )}
      {files.length > 0 && (
        <div className="grid gap-2 mt-2">
          {files.map((f) => <FileRow key={f.id} file={f} onOpen={onOpenFile} onRemove={onRemoveFile} />)}
        </div>
      )}
    </div>
  )
}
